import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { authFetch } from '../../services/api';

// Mirror the lists in MyProperties.jsx so the edit modal renders the same
// options the user saw at create time.
const PROPERTY_TYPES = [
  { value: 'detached', label: 'Detached' },
  { value: 'semi_detached', label: 'Semi-detached' },
  { value: 'terraced', label: 'Terraced' },
  { value: 'end_of_terrace', label: 'End of terrace' },
  { value: 'bungalow', label: 'Bungalow' },
  { value: 'flat', label: 'Flat' },
  { value: 'maisonette', label: 'Maisonette' },
];

const RELATIONSHIPS = [
  { value: 'owner_occupier', label: 'Owner-occupier' },
  { value: 'landlord', label: 'Landlord' },
  { value: 'executor', label: 'Executor' },
  { value: 'developer', label: 'Developer' },
  { value: 'other', label: 'Other' },
];

const TENURES = [
  { value: 'freehold', label: 'Freehold' },
  { value: 'leasehold', label: 'Leasehold' },
  { value: 'shared_ownership', label: 'Shared ownership' },
];

export default function EditPropertyModal({ property, onClose, onSaved }) {
  const [form, setForm] = useState({
    address_line1: property.address_line1 || '',
    address_line2: property.address_line2 || '',
    town: property.town || '',
    postcode: property.postcode || '',
    property_type: property.property_type || 'detached',
    bedrooms: property.bedrooms ?? 3,
    bathrooms: property.bathrooms ?? '',
    relationship_to_property: property.relationship_to_property || 'owner_occupier',
    tenure: property.tenure || 'freehold',
    lease_years_remaining: property.lease_years_remaining ?? '',
    outstanding_debt: property.outstanding_debt ?? '',
  });
  const [saving, setSaving] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.address_line1 || !form.town || !form.postcode) {
      toast.error('Please fill in address, town, and postcode');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        address_line1: form.address_line1.trim(),
        address_line2: form.address_line2.trim() || null,
        town: form.town.trim(),
        postcode: form.postcode.trim().toUpperCase(),
        property_type: form.property_type,
        bedrooms: parseInt(form.bedrooms, 10),
        bathrooms: form.bathrooms === '' ? null : parseInt(form.bathrooms, 10),
        relationship_to_property: form.relationship_to_property,
        tenure: form.tenure,
        lease_years_remaining:
          form.tenure === 'leasehold' && form.lease_years_remaining !== ''
            ? parseInt(form.lease_years_remaining, 10)
            : null,
        outstanding_debt:
          form.outstanding_debt === '' ? null : parseFloat(form.outstanding_debt),
      };
      const res = await authFetch(`/api/valuation/properties/${property.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save property');
      }
      toast.success('Property updated');
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-2xl rounded-2xl bg-slate-900 p-6 border border-slate-800 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white">Edit property</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-white"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Address line 1</label>
              <input
                type="text"
                value={form.address_line1}
                onChange={set('address_line1')}
                required
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Address line 2 (optional)</label>
              <input
                type="text"
                value={form.address_line2}
                onChange={set('address_line2')}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Town</label>
                <input
                  type="text"
                  value={form.town}
                  onChange={set('town')}
                  required
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Postcode</label>
                <input
                  type="text"
                  value={form.postcode}
                  onChange={set('postcode')}
                  required
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white uppercase"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Property type</label>
                <select
                  value={form.property_type}
                  onChange={set('property_type')}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                >
                  {PROPERTY_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Relationship</label>
                <select
                  value={form.relationship_to_property}
                  onChange={set('relationship_to_property')}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                >
                  {RELATIONSHIPS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Bedrooms</label>
                <input
                  type="number"
                  min="0"
                  value={form.bedrooms}
                  onChange={set('bedrooms')}
                  required
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Bathrooms</label>
                <input
                  type="number"
                  min="0"
                  value={form.bathrooms}
                  onChange={set('bathrooms')}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Tenure</label>
                <select
                  value={form.tenure}
                  onChange={set('tenure')}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                >
                  {TENURES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              {form.tenure === 'leasehold' && (
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Lease years remaining</label>
                  <input
                    type="number"
                    min="0"
                    value={form.lease_years_remaining}
                    onChange={set('lease_years_remaining')}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                  />
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Outstanding debt (£)
                <span className="text-slate-500 text-xs ml-1">
                  (mortgage / bridging / secured loan balance — optional)
                </span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.outstanding_debt}
                onChange={set('outstanding_debt')}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
              />
              <p className="text-xs text-slate-500 mt-1">
                Used to calculate Loan-to-Value (LTV) against this property's indicative value range.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
